import './styles/SnapshotManagement.css';
import {
  connectRunningSnapshot,
  connectRunningSnapshotBandwidth,
  connectRunningSnapshotInProgress,
  connectRunningSnapshotNewDevice,
  connectRunningSnapshotServiceLogs,
  disconnectRunningSnapshot,
  ipfSocket,
  joinRoom,
  leaveRoom,
  rooms,
} from 'components/Socket/ipfSocket';
import {
  JOB_ACTIONS,
  JOB_STATUSES,
} from 'containers/App/Header/JobManagement/constants';
import { Map as ImmutableMap, List } from 'immutable';
import { Fragment, PureComponent } from 'react';
import Menu from './Menu';
import { inventoryTab, queueTab, serviceLogsTab } from './Tabs/Tabs';
import {
  changeSnapshot,
  clearState,
  closeSettingsNoPasswords,
  fetchDiscoveryInit,
  fetchSnapshotManagement,
  openNewSettings,
  openSettings,
  setActiveMenuTab,
} from './actions';
import {
  DELETE_DIALOGUE_NAME,
  MENU_TAB_FILTERS,
  PAGE_URL,
  SETTINGS_NO_PASSWORD_DIALOGUE_NAME,
} from './constants';
import { isCurrentSnapshotRunning, isSnapshotLoaded } from './helpers';
import {
  makeSelectAllSnapshots,
  makeSelectDataLoading,
  makeSelectInvalidPasswords,
  makeSelectedSnapshot,
} from './selectors';

import { isNonNullable } from '@ipfabric/utils';
import { makeSelectJobs } from 'containers/App/Header/JobManagement/selectors';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import { selectorConfirmDialogDisplay } from 'containers/ConfirmDialog/selectors';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { equals, pipe } from 'ramda';
import ImmutableProptypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import { createStructuredSelector } from 'reselect';
import fetch from 'utils/fetch';
import { withAppProps } from 'utils/react/withAppProps';
import handleEvent from '../../utils/handleEvent';
import Content from './Content';
import { SettingsPage } from './SettingsPage';
import settingsOpen from './settingsOpen';

@fetch({ fetchOnLocationChange: false, actions: [fetchSnapshotManagement] })
class SnapshotManagement extends PureComponent {
  static whyDidYouRender = true;

  static propTypes = {
    dataLoading: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    invalidPasswords: ImmutableProptypes.map.isRequired,
    jobs: ImmutableProptypes.list.isRequired,
    msg: PropTypes.object.isRequired,
    onChangeSnapshot: PropTypes.func.isRequired,
    onClearState: PropTypes.func.isRequired,
    onCloseSettingsNoPasswords: PropTypes.func.isRequired,
    onFetchDiscoveryInit: PropTypes.func.isRequired,
    onFetchSnapshotManagement: PropTypes.func.isRequired,
    onOpenNewSettings: PropTypes.func.isRequired,
    onOpenSettings: PropTypes.func.isRequired,
    onSetActiveMenuTab: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    selectedSnapshot: PropTypes.string,
    showConfirmDialogue: ImmutableProptypes.map.isRequired,
    snapshots: ImmutableProptypes.list.isRequired,
    showSettings: PropTypes.bool,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
    socketState: PropTypes.string.isRequired,
  };

  static defaultProps = {
    showSettings: false,
  };

  static getDerivedStateFromProps(props, state) {
    if (!equals(get(props, 'params.snapshotId'), state.previousSnapshotId)) {
      const snapshotId = get(props, 'params.snapshotId');
      const newIds = isNonNullable(snapshotId)
        ? new List(snapshotId.split(','))
        : new List();
      if (!newIds.equals(state.currentSnapshotId)) {
        return {
          previousSnapshotId: state.currentSnapshotId,
          currentSnapshotId: newIds,
        };
      }
    }
    return null;
  }

  state = {
    currentSnapshotId: new List(),
    filterName: '',
    previousSnapshotId: new List(),
    selectedSite: new List(),
  };

  componentDidMount() {
    const {
      selectedSnapshot,
      showSettings,
      onOpenNewSettings,
      onOpenSettings,
      location,
      params,
    } = this.props;
    const { currentSnapshotId } = this.state;
    const content = document.querySelector('#snapshot-management-content');
    if (content) {
      // Delay content show to trigger animation
      window.setTimeout(() => {
        content.classList.add('showcontent');
      }, 0);
    }
    if (!currentSnapshotId.size) {
      if (selectedSnapshot) {
        this.props.dispatch(push(`${PAGE_URL}/${selectedSnapshot}`));
      } else {
        this.props.dispatch(push(`${PAGE_URL}/new`));
      }
    }
    if (showSettings) {
      const newSnapshot = get(params, 'snapshotId') === 'new';
      if (newSnapshot) {
        onOpenNewSettings({ disableRedirect: true });
      } else {
        onOpenSettings({ location, disableRedirect: true });
      }
    }
    this.joinSocket();

    ipfSocket.socket.on('authenticated', this.handleSocketAuthenticated);
    ipfSocket.socket.on('disconnect', this.disconnectUserFromSocket);
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
  componentDidUpdate(prevProps) {
    const {
      dataLoading,
      jobs,
      navigate,
      onFetchDiscoveryInit,
      onFetchSnapshotManagement,
      selectedSnapshot,
      snapshots,
      socketState,
    } = this.props;
    const { currentSnapshotId, previousSnapshotId } = this.state;
    const snapshotsIds = snapshots.reduce((acc, item) => {
      acc.push(item.get('id'));
      return acc;
    }, []);
    if (
      prevProps.socketState !== socketState &&
      prevProps.socketState === 'DISCONNECTED'
    ) {
      onFetchSnapshotManagement();
    }
    const showContent = (show) => {
      const content = document.querySelector('#snapshot-management-content');
      if (content) {
        content.classList.add(show ? 'showcontent' : 'hidecontent');
        content.classList.remove(show ? 'hidecontent' : 'showcontent');
      }
    };
    if (
      selectedSnapshot !== prevProps.selectedSnapshot &&
      snapshotsIds.indexOf(selectedSnapshot) > -1
    ) {
      showContent(true);
      if (previousSnapshotId.get(0) !== 'new') {
        onFetchDiscoveryInit();
      }
      if (selectedSnapshot !== currentSnapshotId.get(0)) {
        const foundSnapshot = snapshots.find(
          (snapshot) => snapshot.get('id') === selectedSnapshot,
        );
        const loadingJob =
          foundSnapshot &&
          jobs.find(
            (job) =>
              job.get('snapshot') === foundSnapshot.get('id') &&
              job.get('status') === JOB_STATUSES.running &&
              job.get('action') === JOB_ACTIONS.snapshotLoad,
          );
        const discoveryRunning =
          foundSnapshot &&
          jobs.find(
            (job) =>
              job.get('snapshot') === foundSnapshot.get('id') &&
              job.get('status') === JOB_STATUSES.running &&
              job.get('action') === JOB_ACTIONS.discovery,
          );
        const topologyCalculationRunning =
          foundSnapshot &&
          jobs.find(
            (job) =>
              job.get('snapshot') === foundSnapshot.get('id') &&
              job.get('status') === JOB_STATUSES.running &&
              job.get('action') === JOB_ACTIONS.topologyCalculation,
          );
        const snapshotRunning = !!(
          isCurrentSnapshotRunning(foundSnapshot) ||
          loadingJob ||
          discoveryRunning ||
          topologyCalculationRunning
        );
        navigate(`${PAGE_URL}/${selectedSnapshot}`, {
          state: {
            canLeave: previousSnapshotId.get(0) === 'new' && snapshotRunning,
          },
          replace: true,
        });
      }
    } else if (
      dataLoading !== prevProps.dataLoading &&
      !dataLoading &&
      currentSnapshotId.size
    ) {
      showContent(true);
    }
    if (
      currentSnapshotId.size === 1 &&
      currentSnapshotId.get(0) !== 'new' &&
      !snapshots.find(
        (snapshot) => snapshot.get('id') === currentSnapshotId.get(0),
      )
    ) {
      navigate(`${PAGE_URL}/new`, { replace: true });
    } else if (
      previousSnapshotId.size === 1 &&
      currentSnapshotId.size === 1 &&
      previousSnapshotId.get(0) === 'new' &&
      currentSnapshotId.get(0) !== 'new'
    ) {
      onFetchDiscoveryInit();
    }
    if (currentSnapshotId.size > 1) {
      const validSnapshots = currentSnapshotId.some((id) =>
        snapshots.some((snapshot) => snapshot.get('id') === id),
      );
      if (!validSnapshots) {
        const foundLoadedSnapshot = snapshots.find(
          (snapshot) => snapshot.get('state') === 'loaded',
        );
        if (!foundLoadedSnapshot) {
          navigate(`${PAGE_URL}/new`, { replace: true });
        } else {
          navigate(`${PAGE_URL}/${foundLoadedSnapshot.get('id')}`, {
            replace: true,
          });
        }
      }
    }
    this.joinSocket();
  }

  componentWillUnmount() {
    this.leaveSocket(true);
    const { showConfirmDialogue, onToggleConfirmDialog, onClearState } =
      this.props;
    if (showConfirmDialogue?.get(DELETE_DIALOGUE_NAME)) {
      onToggleConfirmDialog(DELETE_DIALOGUE_NAME);
    }
    onClearState();

    ipfSocket.socket.off('authenticated', this.handleSocketAuthenticated);
    ipfSocket.socket.off('disconnect', this.disconnectUserFromSocket);
  }

  getSnapshots = () => {
    const { snapshots } = this.props;
    const { selectedSite, filterName } = this.state;
    if (!selectedSite.size && filterName === '') {
      return snapshots;
    }
    return pipe(
      (data) => {
        if (!selectedSite.size) {
          return data;
        }
        return data.filter((snapshot) => {
          const sites = snapshot.get('sites');
          if (!sites) {
            return false;
          }
          return (
            sites.filter((site) =>
              selectedSite.find((s) => s.get('uid') === site.get('uid')),
            ).size === selectedSite.size
          );
        });
      },
      (data) => {
        if (filterName === '') {
          return data;
        }
        const input = filterName.toLocaleLowerCase();
        return data.filter((snapshot) => {
          const name = snapshot.get('name');
          if (!name) {
            return false;
          }
          return name.toLocaleLowerCase().indexOf(input) > -1;
        });
      },
    )(snapshots);
  };

  getFormattedMessage = (txt) => txt;

  getSettingsNoPasswordDialogProps = () => {
    const {
      msg: { snapshotManagement: msg },
      invalidPasswords,
    } = this.props;
    const liItems = [];
    if (invalidPasswords.get('credentials')) {
      liItems.push(
        <li key='credentials'>
          <FormattedMessage
            id='settings.nopassword.credentials'
            defaultMessage={`{credentials, plural, one {${get(msg, 'settingsNoPasswordDialog.credential')}} other {${get(msg, 'settingsNoPasswordDialog.credentials')}}}`}
            values={{ credentials: invalidPasswords.get('credentials') }}
          >
            {this.getFormattedMessage}
          </FormattedMessage>
        </li>,
      );
    }
    if (invalidPasswords.get('privileges')) {
      liItems.push(
        <li key='enablePasswords'>
          <FormattedMessage
            id='settings.nopassword.enablePasswords'
            defaultMessage={`{privileges, plural, one {${get(msg, 'settingsNoPasswordDialog.enablePassword')}} other {${get(msg, 'settingsNoPasswordDialog.enablePasswords')}}}`}
            values={{ privileges: invalidPasswords.get('privileges') }}
          >
            {this.getFormattedMessage}
          </FormattedMessage>
        </li>,
      );
    }

    return {
      bodyComponent: (
        <Fragment>
          <h5>{get(msg, 'settingsNoPasswordDialog.subTitle', '')}</h5>
          <ul>{liItems}</ul>
        </Fragment>
      ),
      msg: {
        title: <span>{get(msg, 'settingsNoPasswordDialog.title', '')}</span>,
        btnConfirm: get(msg, 'settingsNoPasswordDialog.btnConfirm'),
      },
    };
  };

  getContent = () => {
    const {
      jobs,
      params,
      msg: {
        snapshotManagement: msg,
        exceptions: exceptionsMsg,
        hostnameTableDetail: hostnameTableDetailMsg,
      },
      msg: allMsg,
      onCloseSettingsNoPasswords,
      showSettings,
      location,
    } = this.props;
    const { selectedSite, currentSnapshotId } = this.state;
    const filteredSnapshots = this.getSnapshots();
    const foundSnapshot = filteredSnapshots.find(
      (snapshot) => snapshot.get('id') === currentSnapshotId.get(0),
    );
    const loadingJob =
      foundSnapshot &&
      jobs.find(
        (job) =>
          job.get('snapshot') === foundSnapshot.get('id') &&
          job.get('status') === JOB_STATUSES.running &&
          job.get('action') === JOB_ACTIONS.snapshotLoad,
      );
    const snapshotFinishing =
      foundSnapshot !== undefined &&
      jobs.some(
        (job) =>
          job.get('snapshot') === foundSnapshot.get('id') &&
          job.get('status') === JOB_STATUSES.running &&
          (job.get('action') === JOB_ACTIONS.saveHistoricalData ||
            job.get('action') === JOB_ACTIONS.report ||
            job.get('action') === JOB_ACTIONS.loadGraphCache ||
            job.get('action') === JOB_ACTIONS.topologyCalculation),
      );
    const discoveryRunning =
      foundSnapshot &&
      jobs.find(
        (job) =>
          job.get('snapshot') === foundSnapshot.get('id') &&
          job.get('status') === JOB_STATUSES.running &&
          job.get('action') === JOB_ACTIONS.discovery,
      );
    const topologyCalculationRunning =
      foundSnapshot &&
      jobs.find(
        (job) =>
          job.get('snapshot') === foundSnapshot.get('id') &&
          job.get('status') === JOB_STATUSES.running &&
          job.get('action') === JOB_ACTIONS.topologyCalculation,
      );
    const snapshotRunning = !!(
      isCurrentSnapshotRunning(foundSnapshot) ||
      loadingJob ||
      discoveryRunning ||
      topologyCalculationRunning
    );
    const scheduledJob =
      foundSnapshot &&
      jobs.some(
        (job) =>
          (job.get('snapshot') === foundSnapshot.get('id') ||
            job.getIn(['args', 'vCollectKey']) === foundSnapshot.get('id')) &&
          job.get('status') === JOB_STATUSES.scheduled &&
          job.get('action') === JOB_ACTIONS.discovery,
      );
    const recalculating =
      foundSnapshot &&
      jobs.some(
        (job) =>
          (job.get('snapshot') === foundSnapshot.get('id') ||
            job.getIn(['args', 'vCollectKey']) === foundSnapshot.get('id')) &&
          job.get('action') === JOB_ACTIONS.recalculateSites &&
          job.get('status') !== JOB_STATUSES.error &&
          job.get('status') !== JOB_STATUSES.scheduled,
      );
    const snapshotLoading = foundSnapshot?.get('state') === 'loading';
    const newSnapshot = get(params, 'snapshotId') === 'new';
    return (
      <Fragment>
        <ConfirmDialog
          id={SETTINGS_NO_PASSWORD_DIALOGUE_NAME}
          closable={false}
          onClickConfirm={onCloseSettingsNoPasswords}
          btnConfirmDataTestId='snapshot-management-no-password-continue-button'
          {...this.getSettingsNoPasswordDialogProps()}
        />
        <SettingsPage
          foundSnapshot={foundSnapshot?.toJS()}
          newSnapshot={newSnapshot}
          onClose={this.handleCloseSettings}
          showSettings={showSettings}
        />
        <Menu
          currentSnapshotId={currentSnapshotId}
          filteredSnapshots={filteredSnapshots}
          msg={msg}
          onAddMultipleItems={this.handleAddMultipleItems}
          onFilterNameChange={this.handleFilterNameChange}
          onSelectSite={this.handleSelectSite}
          selectedSite={selectedSite}
        />
        <Content
          allMsg={allMsg}
          currentSnapshotId={currentSnapshotId}
          exceptionsMsg={exceptionsMsg}
          hostnameTableDetailMsg={hostnameTableDetailMsg}
          loadingJob={!!loadingJob}
          location={location}
          msg={msg}
          newSnapshot={newSnapshot}
          onAddLabel={this.handleAddLabel}
          onClearSitesFilter={this.handleClearSitesFilter}
          onCloseSettings={this.handleCloseSettings}
          recalculating={recalculating}
          scheduledJob={scheduledJob}
          selectedSite={selectedSite}
          showSettings={showSettings}
          snapshotFinishing={snapshotFinishing}
          snapshotFromParamsIsInvalid={this.snapshotFromParamsIsInvalid()}
          snapshotId={foundSnapshot?.get('id')}
          snapshotLoading={snapshotLoading}
          snapshotRunning={snapshotRunning}
          snapshotState={foundSnapshot?.get('state')}
          snapshotStatus={foundSnapshot?.get('status')}
        />
      </Fragment>
    );
  };

  handleCloseSettings = () => {
    const { navigate, selectedSnapshot } = this.props;
    const newSnapshot = get(this.props.params, 'snapshotId') === 'new';
    if (newSnapshot) {
      navigate(`${PAGE_URL}/new`, { replace: true });
    } else {
      navigate(
        { pathname: `${PAGE_URL}/${selectedSnapshot}`, search: '', hash: '' },
        { replace: true },
      );
    }
  };

  handleSocketAuthenticated = () => {
    this.joinSocket(true);
  };

  disconnectUserFromSocket = () => {
    this.leaveSocket(true);
  };

  snapshotFromParamsIsInvalid = () => {
    const { selectedSnapshot, params, snapshots } = this.props;
    const { currentSnapshotId } = this.state;
    const newSnapshot = get(params, 'snapshotId') === 'new';
    return (
      currentSnapshotId.size === 1 &&
      !newSnapshot &&
      selectedSnapshot &&
      !snapshots.some(
        (snapshot) => snapshot.get('id') === currentSnapshotId.get(0),
      )
    );
  };

  shouldListenToSocket = () => {
    const { jobs } = this.props;
    const { currentSnapshotId } = this.state;
    return (
      currentSnapshotId.size === 1 &&
      jobs.find(
        (job) =>
          job.get('snapshot') === currentSnapshotId.get(0) &&
          !job.get('done') &&
          (job.get('action') === JOB_ACTIONS.discovery ||
            job.get('action') === JOB_ACTIONS.snapshotLoad ||
            // Topology calculation temporarily logs into the tasker logs, so sockets must remain active.
            job.get('action') === JOB_ACTIONS.topologyCalculation),
      )
    );
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
  joinSocket = (socketReconnected = false) => {
    const { dispatch, location } = this.props;
    const hash = get(location, 'hash');
    this.leaveSocket();
    if (!this.socketJoined || hash !== this.socketHash || socketReconnected) {
      const foundJob = this.shouldListenToSocket();
      if (foundJob) {
        if (!this.socketJoined || socketReconnected) {
          const jobAction = foundJob.get('action');
          joinRoom(rooms.system);
          connectRunningSnapshot(dispatch);
          if (jobAction === JOB_ACTIONS.discovery) {
            connectRunningSnapshotBandwidth(dispatch);
          }
        }
        if (hash === `#${queueTab}`) {
          connectRunningSnapshotInProgress(dispatch);
        } else if (hash === `#${serviceLogsTab}`) {
          connectRunningSnapshotServiceLogs(dispatch);
        } else if (hash === `#${inventoryTab}` || hash === '') {
          connectRunningSnapshotNewDevice(dispatch);
        }
        this.socketJoined = true;
        this.socketHash = hash;
      }
    }
  };

  leaveSocket = (forced = false) => {
    if (forced || (this.socketJoined && !this.shouldListenToSocket())) {
      disconnectRunningSnapshot();
      leaveRoom(rooms.system);
      this.socketJoined = false;
    }
  };

  handleAddLabel = handleEvent((e) => {
    const id = e.target.getAttribute('data-id');
    const siteName = e.target.getAttribute('data-name');
    if (id && siteName) {
      const { onSetActiveMenuTab } = this.props;
      const { selectedSite } = this.state;
      const foundIndex = selectedSite.findIndex(
        (site) => site.get('uid') === id,
      );
      onSetActiveMenuTab(MENU_TAB_FILTERS);
      if (foundIndex > -1) {
        this.setState({ selectedSite: selectedSite.delete(foundIndex) });
      } else {
        this.setState({
          selectedSite: selectedSite.push(
            new ImmutableMap({ uid: id, siteName }),
          ),
        });
      }
    }
  });

  handleClearSitesFilter = handleEvent(() => {
    this.setState({ selectedSite: new List() });
  });

  handleFilterNameChange = handleEvent(({ target: { value } }) => {
    this.setState({ filterName: value });
  });

  handleAddMultipleItems = handleEvent((ids) => {
    // If only one snapshot is selected, select the snapshot as current for whole app
    if (ids && ids.length === 1) {
      const { currentSnapshotId } = this.state;
      const foundSnapshot = this.props.snapshots.find(
        (item) => item.get('id') === ids[0],
      );
      if (foundSnapshot && isSnapshotLoaded(foundSnapshot.get('state'))) {
        this.props.onChangeSnapshot(ids[0]);
      }
      const content = document.querySelector('#snapshot-management-content');
      if (content && !currentSnapshotId.size) {
        content.classList.add('showcontent');
        content.classList.remove('hidecontent');
      }
      const foundIndex = currentSnapshotId.indexOf(ids[0]);
      if (foundIndex === -1 || currentSnapshotId.size > 1) {
        this.props.navigate(`${PAGE_URL}/${ids[0]}`, { replace: true });
      }
    } else {
      this.props.navigate(`${PAGE_URL}/${ids.join(',')}`, { replace: true });
    }
  });

  handleSelectSite = handleEvent((list) => {
    this.setState({ selectedSite: list });
  });

  render() {
    const {
      dataLoading,
      msg: { snapshotManagement: msg },
    } = this.props;

    return (
      <div className='snapshots'>
        {dataLoading ? (
          <div className='flex flex-1 flex-col items-center justify-center'>
            <LoaderAndText bottomText={get(msg, 'loading')} />
          </div>
        ) : (
          this.getContent()
        )}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  dataLoading: makeSelectDataLoading(),
  selectedSnapshot: makeSelectedSnapshot(),
  showConfirmDialogue: selectorConfirmDialogDisplay(),
  snapshots: makeSelectAllSnapshots(),
  jobs: makeSelectJobs(),
  invalidPasswords: makeSelectInvalidPasswords(),
});

function mapDispatchToProps(dispatch) {
  return {
    onChangeSnapshot: (args) => dispatch(changeSnapshot(args)),
    onClearState: (args) => dispatch(clearState(args)),
    onFetchDiscoveryInit: (args) => dispatch(fetchDiscoveryInit(args)),
    onFetchSnapshotManagement: (args) =>
      dispatch(fetchSnapshotManagement(args)),
    onSetActiveMenuTab: (args) => dispatch(setActiveMenuTab(args)),
    onOpenNewSettings: (args) => dispatch(openNewSettings(args)),
    onOpenSettings: (args) => dispatch(openSettings(args)),
    onCloseSettingsNoPasswords: () => dispatch(closeSettingsNoPasswords()),
    onToggleConfirmDialog: (args) => dispatch(toggleConfirmDialog(args)),
    dispatch,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withAppProps(settingsOpen(SnapshotManagement)));
